// ReorderPanel — "Below reorder threshold" read-only report (ERP chunk 1,
// phase 8, decision 10). Moved here from the Receiving Hub (Mannie, final:
// "POs on the PO page, Receiving on the Receiving page"): this is a
// purchasing signal, the same tier as creating a PO, not a receiving concern.
// Mounted on PurchaseOrderList, below the list.
//
// READ-ONLY report: a part is listed when its target's projected stock
// (on_hand + on_order - what open work orders still need) is at or below the
// target's threshold. Family members are pooled into one target. The bar, the
// stat line and the Draft a PO quantity all read the target, never the row's
// own part. See ReorderSuggestionRow in poApi.ts for the exact contract.
//
// "Draft a PO" hands the suggestion to PurchaseOrderForm via sessionStorage
// (REORDER_PREFILL_SESSION_KEY, owned by poApi.ts — the one place every PO
// API-shape assumption lives) and navigates to /procurement/new, which reads
// the prefill on mount and clears the key.
//
// No internal capability check: PurchaseOrderList's route is already
// procurement|admin-gated in App.js, so anyone who can mount this panel could
// already see it.

import React, { useEffect, useState } from 'react';
import Appear from '../components/ui/Appear';
import { buttonClass } from '../components/ui/Button';
import FadeIn from '../components/ui/FadeIn';
import { EmptyState } from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import { Progress } from '../components/ui/Progress';
import { Skeleton } from '../components/ui/Skeleton';
import { useHistory } from 'react-router-dom';
import { useToast } from '../components/Toast/ToastContext';
// Name-first: this is an internal purchasing signal, so no SKU line. The buyer
// recognizes "Gen2 Pyro (24V, rev 1)", not FF-PYR-02-1-CH.
import ItemName from '../components/ItemName';
import {
  usePoApi,
  REORDER_PREFILL_SESSION_KEY,
  type ReorderSuggestionRow,
  type ReorderDraftPrefill,
} from './poApi';

// YYYY-MM-DD as a LOCAL date: new Date('2026-12-01') parses as UTC and shows
// the day before in US timezones.
function shortDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function arrivalLine(t: ReorderSuggestionRow['target']): string {
  if (t.next_arrival) {
    const po = t.next_arrival.po_number ? ` on ${t.next_arrival.po_number}` : '';
    return `Next in: ${t.next_arrival.quantity}${po}, expected ${shortDate(t.next_arrival.date)}`;
  }
  if (t.on_order > 0) return 'On order, no date given yet';
  if (t.order_today_lands) {
    return `Not on order. Ordered today, lands about ${shortDate(t.order_today_lands)}`;
  }
  return 'Not on order, no lead time set';
}

const ReorderPanel: React.FC = () => {
  const poApi = usePoApi();
  const toast = useToast();
  const history = useHistory();

  const [suggestions, setSuggestions] = useState<ReorderSuggestionRow[]>([]);
  const [loading, setLoading] = useState(true);
  // Default open when there's something to act on; stay collapsed (out of
  // the way) when the fleet is fully stocked.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await poApi.reorderSuggestions();
        if (cancelled) return;
        const rows = data.rows || [];
        setSuggestions(rows);
        if (rows.length > 0) setExpanded(true);
      } catch (err: any) {
        if (!cancelled) toast.error(err?.message || 'Failed to load reorder suggestions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stash the suggestion and go straight to the PO create page, which reads
  // this key on mount. See REORDER_PREFILL_SESSION_KEY in poApi.ts.
  const handleDraftPo = (row: ReorderSuggestionRow) => {
    const shortfall = row.target.threshold - row.target.projected;
    const prefill: ReorderDraftPrefill = {
      sku: row.sku,
      part_catalog_id: row.part_catalog_id,
      quantity: Math.max(shortfall, 1),
      vendor_name: row.suggested_vendor,
    };
    try {
      sessionStorage.setItem(REORDER_PREFILL_SESSION_KEY, JSON.stringify(prefill));
    } catch {
      // sessionStorage can throw under storage-capped/locked-down browser
      // contexts — worst case the form just sees no prefill.
    }
    history.push('/procurement/new');
  };

  return (
    <Panel
      padding="none"
      title={
        <>
          Below reorder threshold
          <Appear show={suggestions.length > 0} className="ml-2 text-amber-400 font-normal">
            ({suggestions.length})
          </Appear>
        </>
      }
      collapse={{ open: expanded, onToggle: () => setExpanded((v) => !v) }}
    >
      <div className="divide-y divide-outline-variant">
        {loading ? (
          <div role="status" aria-label="Loading reorder suggestions" className="space-y-3 px-4 py-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : suggestions.length === 0 ? (
          <FadeIn key="empty"><EmptyState variant="area">Nothing below threshold right now.</EmptyState></FadeIn>
        ) : (
          <FadeIn key="rows" className="divide-y divide-outline-variant">
          {suggestions.map((s) => {
            const t = s.target;
            const pct =
              t.threshold > 0
                ? Math.max(0, Math.min(100, Math.round((t.projected / t.threshold) * 100)))
                : 0;
            return (
              <div key={s.part_catalog_id} className="px-4 py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  {/* Block, not inline: ItemName truncates itself only in
                      block form, and this row lives in a min-w-0 flex column
                      where a long name would otherwise push the bar out. */}
                  <ItemName size="sm" item={s} />
                  <Progress
                    className="mt-1.5 max-w-xs"
                    size="md"
                    label="Projected stock against reorder threshold"
                    value={pct}
                    max={100}
                  />
                  {/* "with vendor" sits outside the sum on purpose: those
                      units are neither on the shelf nor on their way, because
                      the vendor has not said yet whether they are sending
                      replacements or sending money. The bar above ignores
                      them for the same reason. */}
                  <div className="mt-1 text-[11px] text-on-surface-variant tabular-nums">
                    {t.on_hand} on hand
                    {t.on_order > 0 ? ` + ${t.on_order} on order` : ''}
                    {t.committed > 0 ? ` − ${t.committed} for work orders` : ''}
                    {t.on_order > 0 || t.committed > 0 ? ` = ${t.projected} left` : ''}
                    {s.with_vendor > 0 ? ` · ${s.with_vendor} with vendor` : ''} · threshold{' '}
                    {t.threshold}
                    {s.suggested_vendor ? ` · last from ${s.suggested_vendor}` : ''}
                  </div>
                  {t.family_label && (
                    <div className="mt-0.5 text-[11px] text-on-surface-variant">
                      Counted with the {t.family_label} family
                    </div>
                  )}
                  <div className="mt-0.5 text-[11px] text-on-surface-variant tabular-nums">
                    {arrivalLine(t)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleDraftPo(s)}
                  className={`${buttonClass('quiet', 'sm')} shrink-0`}
                >
                  Draft a PO
                </button>
              </div>
            );
          })}
          </FadeIn>
        )}
      </div>
    </Panel>
  );
};

export default ReorderPanel;
